'use client';

import { useEffect, useRef } from 'react';

import { usePathname } from 'next/navigation';

import { EPortalRumEvents, logPortalRumError, logPortalRumInfo } from '@/lib/coralogix';
import { readLandingJourneyId } from '@/lib/landingJourney';
import { webStorageUtil } from '@/state/webStorage.state.service';

export const DEFAULT_LONG_LOADING_THRESHOLD_MILLISECONDS = 10_000;

interface IUseLongLoadingRumProps {
  isLoading: boolean;
  loadingArea: string;
  thresholdMilliseconds?: number;
  context?: Record<string, unknown>;
}

/**
 * Reports loading episodes that exceed their expected duration and later recover.
 *
 * @example
 * useLongLoadingRum({
 *   isLoading: getOffers.isLoading,
 *   loadingArea: 'store-offers',
 *   context: { queryStatus: getOffers.status },
 * });
 */
export const useLongLoadingRum = ({
  isLoading,
  loadingArea,
  thresholdMilliseconds = DEFAULT_LONG_LOADING_THRESHOLD_MILLISECONDS,
  context = {},
}: IUseLongLoadingRumProps): void => {
  const pathname = usePathname();
  const contextRef = useRef(context);
  const loadingStartedAtRef = useRef<number | undefined>(undefined);
  const didReportStallRef = useRef(false);

  contextRef.current = context;

  useEffect(() => {
    if (!isLoading) {
      if (loadingStartedAtRef.current !== undefined && didReportStallRef.current) {
        logPortalRumInfo(
          EPortalRumEvents.PORTAL_LOADING_RECOVERED,
          createLoadingRumData({
            context: contextRef.current,
            durationMilliseconds: Date.now() - loadingStartedAtRef.current,
            loadingArea,
            pathname,
          })
        );
      }

      loadingStartedAtRef.current = undefined;
      didReportStallRef.current = false;

      return;
    }

    const loadingStartedAt = Date.now();
    loadingStartedAtRef.current = loadingStartedAt;
    didReportStallRef.current = false;

    const timeoutId = window.setTimeout(() => {
      didReportStallRef.current = true;
      logPortalRumError(
        EPortalRumEvents.PORTAL_LOADING_STALLED,
        createLoadingRumData({
          context: contextRef.current,
          durationMilliseconds: thresholdMilliseconds,
          loadingArea,
          pathname,
        })
      );
    }, thresholdMilliseconds);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoading, loadingArea, pathname, thresholdMilliseconds]);
};

interface ICreateLoadingRumDataProps {
  context: Record<string, unknown>;
  durationMilliseconds: number;
  loadingArea: string;
  pathname: string;
}

const createLoadingRumData = ({
  context,
  durationMilliseconds,
  loadingArea,
  pathname,
}: ICreateLoadingRumDataProps): Record<string, unknown> => ({
  ...context,
  appSessionId: webStorageUtil.getStorageWithFallback()?.id,
  journeyId: readLandingJourneyId(),
  durationMilliseconds,
  isOnline: navigator.onLine,
  loadingArea,
  pathname,
  visibilityState: document.visibilityState,
});
